import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  CircularProgress,
  Divider,
  Chip,
  SelectChangeEvent,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../store";
import { sendMessageToAgent } from "../../../store/userSlice";
import { v4 as uuid } from "uuid";
import MarkdownRenderer from "../../MarkdownRenderer";
import axiosClient from "../../../api/AxiosClient";
import type { SubmitResponse } from "../../../pages/SkillGapAssessment/types";
import styles from "./learning-path-form.module.scss";

type LearningPathUtilityAgentFormProps = {
  parsedContent: any;
  messageId?: string;
};

type SkillTargetLevel = {
  skill: string;
  currentScore: number;
  targetLevel: number;
};

export default function LearningPathUtilityAgentForm({
  parsedContent,
  messageId,
}: LearningPathUtilityAgentFormProps): JSX.Element {
  const dispatch = useAppDispatch();

  // Get current agent and chat from Redux
  const currentAgent = useSelector(
    (state: RootState) => state.agents?.currentAgent,
  );
  const currentChat = useSelector((state: RootState) => state.user.currentChat);
  const params = new URLSearchParams(location.search);
  const sessionId = params.get("sessionId");
  const agentLoading = useSelector((state: RootState) => state.user.loading);

  // Extract question from parsedContent
  const data = parsedContent?.finalAnswer || parsedContent;
  const question = data?.question || parsedContent?.question || "";

  // Check if this is the latest message to show the form
  const latestAgentMessage = [...currentChat]
    .reverse()
    .find((message) => message.sender === "agent");
  const showForm = latestAgentMessage?.id === messageId;

  // Form state - Section 1: Learning Preferences
  const [language, setLanguage] = useState("");

  // Form state - Section 2: Career Path & Goals
  const [currentRole, setCurrentRole] = useState("");
  const [targetRole, setTargetRole] = useState("");
  const [timeline, setTimeline] = useState("");

  // Form state - Section 3: Training History
  const [certifications, setCertifications] = useState("");
  const [previousTraining, setPreviousTraining] = useState("");

  // Form state - Section 4: Proficiency Levels
  const [skillTargetLevels, setSkillTargetLevels] = useState<
    SkillTargetLevel[]
  >([]);
  const [loadingSkills, setLoadingSkills] = useState(false);
  const [skillsError, setSkillsError] = useState<string | null>(null);

  // Fetch skills from skill-gap-information on component mount
  useEffect(() => {
    const fetchSkills = async () => {
      setLoadingSkills(true);
      setSkillsError(null);

      try {
        const response = await axiosClient.get<SubmitResponse>(
          "/skill-gap-information",
        );

        // Extract skills from response
        const skills =
          response.data?.skills || response.data?.result?.data?.skills || [];

        // Initialize skill target levels with current_score and target_level from API
        const initialSkillLevels = skills.map((skill: any) => ({
          skill: skill.skill,
          currentScore: skill.current_score || 0,
          targetLevel: skill.target_level || 0,
        }));

        setSkillTargetLevels(initialSkillLevels);
      } catch (err: any) {
        console.error("Error fetching skills:", err);
        setSkillsError("Unable to load skills. Please try again.");
        setSkillTargetLevels([]);
      } finally {
        setLoadingSkills(false);
      }
    };

    if (showForm) {
      fetchSkills();
    }
  }, [showForm]);

  // Check if required fields are filled
  const isFormValid =
    language.trim() &&
    currentRole.trim() &&
    targetRole.trim() &&
    timeline.trim();

  const handleSubmit = () => {
    if (!isFormValid || !currentAgent) return;

    // Build the submission payload in the required format
    const formDataArray = [
      {
        key: "Preferred_Language",
        question: "",
        answer: [language],
      },
      {
        key: "Current_Role",
        question: "",
        answer: currentRole.trim(),
      },
      {
        key: "Target_Role",
        question: "",
        answer: targetRole.trim(),
      },
      {
        key: "Target_Timeline",
        question: "",
        answer: timeline,
      },
      {
        key: "Relevant_Certifications",
        question: "",
        answer: certifications.trim() || "",
      },
      {
        key: "Previous_Learning_Paths",
        question: "",
        answer: previousTraining.trim() || "",
      },
      {
        key: "Proficiency_Levels",
        question: "",
        skills: skillTargetLevels.map((item) => ({
          skill: item.skill,
          current_score: item.currentScore,
          target_level: item.targetLevel,
        })),
      },
    ];

    const questionId = uuid();
    const timestamp = new Date().toISOString();

    const payload: any = {
      agentId: currentAgent.id,
      message: JSON.stringify(formDataArray),
      questionId,
      timestamp,
      agentEndpoint: currentAgent.endpoint,
      sessionId,
    };

    dispatch(sendMessageToAgent(payload));
  };

  // Shared styles
  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#fff",
      fontSize: "14px",
      "& fieldset": { borderColor: "#d0e8e4" },
      "&:hover fieldset": { borderColor: "#00a888" },
      "&.Mui-focused fieldset": { borderColor: "#00a888" },
    },
  };

  const questionSx = {
    fontWeight: 600,
    fontSize: "13px",
    color: "#004141",
    mb: 1,
  };

  const sectionTitleSx = {
    fontWeight: 700,
    fontSize: "15px",
    color: "#004141",
    mb: 2,
    mt: 3,
  };

  return (
    <Box className={styles.conversationContainer}>
      {/* Question/Prompt - always visible */}
      {question && (
        <Box className={styles.questionBox}>
          <Box className={styles.questionIcon}>
            <InfoOutlinedIcon sx={{ color: "#6b7280", fontSize: 18 }} />
          </Box>
          <Box className={styles.questionContent}>
            <MarkdownRenderer content={question} />
          </Box>
        </Box>
      )}

      {/* Form - only shows for the latest message */}
      {showForm && (
        <Box className={styles.formContainer}>
          {/* Form Header */}
          <Box className={styles.formHeader}>
            <Typography className={styles.formEyebrow}>
              Learning Path Customization
            </Typography>
            <Typography className={styles.formTitle}>
              Tell us about your learning preferences
            </Typography>
            <Typography sx={{ fontSize: "12px", color: "#6b7280", mt: 0.5 }}>
              Complete the form below to generate a personalized learning path
              tailored to your goals.
            </Typography>
          </Box>

          {/* Form Fields */}
          <Box className={styles.formFields}>
            {/* Section 1: Learning Preferences */}
            <Typography sx={sectionTitleSx}>
              1. Employee Learning Preferences
            </Typography>

            <Box mb={2.5}>
              <Typography sx={questionSx}>
                What is your preferred language for learning materials?
                <span style={{ color: "#dc3545" }}> *</span>
              </Typography>
              <FormControl fullWidth size="small" sx={fieldSx}>
                <Select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    Select language
                  </MenuItem>
                  <MenuItem value="English">English</MenuItem>
                  <MenuItem value="Arabic">Arabic</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Section 2: Career Path & Goals */}
            <Typography sx={sectionTitleSx}>2. Career Path & Goals</Typography>

            <Box mb={2.5}>
              <Typography sx={questionSx}>
                What is your current role?
                <span style={{ color: "#dc3545" }}> *</span>
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={currentRole}
                onChange={(e) => setCurrentRole(e.target.value)}
                placeholder="e.g., HR Manager"
                sx={fieldSx}
              />
            </Box>

            <Box mb={2.5}>
              <Typography sx={questionSx}>
                What is your target role or desired career position?
                <span style={{ color: "#dc3545" }}> *</span>
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={targetRole}
                onChange={(e) => setTargetRole(e.target.value)}
                placeholder="e.g., Senior HR Business Partner"
                sx={fieldSx}
              />
            </Box>

            <Box mb={2.5}>
              <Typography sx={questionSx}>
                What is your target timeline for achieving this goal?
                <span style={{ color: "#dc3545" }}> *</span>
              </Typography>
              <FormControl fullWidth size="small" sx={fieldSx}>
                <Select
                  value={timeline}
                  onChange={(e) => setTimeline(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    Select timeline
                  </MenuItem>
                  <MenuItem value="3 Months">3 Months</MenuItem>
                  <MenuItem value="6 Months">6 Months</MenuItem>
                  <MenuItem value="12 Months">12 Months</MenuItem>
                  <MenuItem value="24+ Months">24+ Months</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Section 3: Training History */}
            <Typography sx={sectionTitleSx}>3. Training History</Typography>

            <Box mb={2.5}>
              <Typography sx={questionSx}>
                Do you currently hold any relevant certifications?
                <span style={{ color: "#9ca3af", fontWeight: 400 }}>
                  {" "}
                  (Optional)
                </span>
              </Typography>
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={2}
                value={certifications}
                onChange={(e) => setCertifications(e.target.value)}
                placeholder="e.g., SHRM-CP, PHR, CIPD Level 5"
                sx={fieldSx}
              />
            </Box>

            <Box mb={2.5}>
              <Typography sx={questionSx}>
                Have you previously completed any learning paths or structured
                training programs?
                <span style={{ color: "#9ca3af", fontWeight: 400 }}>
                  {" "}
                  (Optional)
                </span>
              </Typography>
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={2}
                value={previousTraining}
                onChange={(e) => setPreviousTraining(e.target.value)}
                placeholder="e.g., LinkedIn Learning HR Fundamentals, Coursera Talent Management"
                sx={fieldSx}
              />
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Section 4: Proficiency Levels */}
            <Typography sx={sectionTitleSx}>4. Proficiency Levels</Typography>

            <Box
              sx={{
                backgroundColor: "#f9fafb",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                p: 2,
                mb: 2,
              }}
            >
              <Typography sx={{ fontSize: "12px", color: "#6b7280", mb: 1 }}>
                Proficiency Scale:
              </Typography>
              <Typography sx={{ fontSize: "12px", color: "#4b5563" }}>
                1 = Beginner | 2 = Basic | 3 = Intermediate | 4 = Advanced | 5 =
                Expert
              </Typography>
            </Box>

            {loadingSkills && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={24} sx={{ color: "#00a888" }} />
              </Box>
            )}

            {skillsError && (
              <Typography sx={{ color: "#dc3545", fontSize: "13px", mb: 2 }}>
                {skillsError}
              </Typography>
            )}

            {!loadingSkills &&
              !skillsError &&
              skillTargetLevels.length === 0 && (
                <Typography sx={{ color: "#6b7280", fontSize: "13px", mb: 2 }}>
                  No skill gaps found. Please complete the skill gap assessment
                  first.
                </Typography>
              )}

            {!loadingSkills &&
              !skillsError &&
              skillTargetLevels.map((item, index) => (
                <Box
                  key={index}
                  mb={2}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    flexWrap: "wrap",
                  }}
                >
                  <Box sx={{ flex: "1 1 300px", minWidth: "200px" }}>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        color: "#374151",
                        fontWeight: 500,
                      }}
                    >
                      {item.skill}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: "0 0 200px" }}>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        color: "#374151",
                        fontWeight: 500,
                      }}
                    >
                      {item.currentScore}
                    </Typography>
                  </Box>
                </Box>
              ))}
          </Box>

          {/* Submit Button */}
          <Box className={styles.formActions}>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!isFormValid || agentLoading}
              className={styles.submitButton}
            >
              Submit Preferences
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}
